<template>
  <div class="mt-2">
    <v-alert v-if="error" type="error" class="mb-0">
      <v-row align="center">
        <v-col class="grow py-0">
          {{ error }}
        </v-col>
        <v-col class="shrink py-0">
          <v-btn small @click="$emit('refresh')" :disabled="blockRefresh">
            Tentar novamente
          </v-btn>
        </v-col>
      </v-row>
    </v-alert>
    <template v-else-if="(loading && !payments.length) || loadingEssentials">
      <v-progress-linear indeterminate color="info" class="mt-2" />
      <v-alert type="info" text class="mt-0 mb-0">
        Carregando relatório, por favor aguarde...
        <p v-if="oldEvent" class="mb-0">
          Eventos antigos podem demorar mais para carregar
        </p>
      </v-alert>
    </template>
    <v-alert
      v-else-if="!loading && !filteredData?.payments?.length"
      class="mb-0"
      type="info"
      text
    >
      Nenhum pagamento com UTM encontrado
    </v-alert>
    <div v-else class="d-flex flex-column gap-2">
      <div class="px-2 d-flex align-center">
        <v-spacer />
        <v-btn
          @click="$emit('refresh')"
          icon
          small
          :loading="loading"
          :disabled="blockRefresh"
          :class="loading ? 'mr-2' : ''"
        >
          <v-icon small>mdi-refresh</v-icon>
        </v-btn>
        <span class="text-caption" v-if="lastUpdate">
          Última atualização: {{ lastUpdate | date("DD/MM [às] HH:mm:ss") }}
        </span>
      </div>
      <v-alert
        v-if="loading && isOldData"
        class="mb-2"
        icon="mdi-update"
        type="warning"
        text
      >
        Os dados estão {{ [lastUpdate] | dateDiff }} desatualizados. Por favor
        aguarde enquanto buscamos os dados mais recentes.
      </v-alert>

      <cols-organizer md="2" xl="3" :items="reportsComponents">
        <template v-slot="{ item }">
          <component
            :is="item"
            :id="item"
            :party="party"
            :payments="filteredData.payments"
            :tickets="filteredData.tickets"
            :reduced-data="reducedData"
            :loading="loading"
          />
        </template>
      </cols-organizer>

      <v-card outlined :loading="loading" class="mx-2 mt-2">
        <v-card-title class="d-flex justify-space-between pb-0">
          <h6 class="mb-0 lh-1">Compras</h6>
        </v-card-title>
        <v-data-table :headers="headers" :items="filteredData.payments" dense>
          <template v-slot:item.id="{ item }">
            #{{ item.id | shortId }}
          </template>
          <template v-slot:item.createdAt="{ item }">
            {{ item.createdAt | date("DD/MM/YYYY HH:mm") }}
          </template>
          <template v-slot:item.amount="{ item }">
            {{ (item.amount - item.platformFee) | currency }}
          </template>
        </v-data-table>
      </v-card>
    </div>
  </div>
</template>

<script>
import moment from "moment";

import exportExcel from "@/utils/exportExcel/salesResume";
import ColsOrganizer from "../../global/ColsOrganizer.vue";

import PartyReportUTMCampaign from "./reports/PartyReportUTMCampaign.vue";
import PartyReportUTMSource from "./reports/PartyReportUTMSource.vue";

export default {
  name: "PartyResumeReport",
  components: {
    PartyReportUTMCampaign,
    PartyReportUTMSource,
    ColsOrganizer,
  },
  props: {
    party: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loadingEssentials: false,
      exporting: null,
      headers: [
        { text: "ID", value: "id" },
        { text: "Data", value: "createdAt" },
        { text: "Valor", value: "amount" },
        { text: "Campanha", value: "utm.campaign" },
        { text: "Fonte", value: "utm.source" },
        { text: "Medium", value: "utm.medium" },
        { text: "Termo", value: "utm.term" },
        { text: "Conteúdo", value: "utm.content" },
      ],
      reportsComponents: ["PartyReportUTMCampaign", "PartyReportUTMSource"],
    };
  },
  computed: {
    isOldData() {
      return (
        this.lastUpdate &&
        moment(this.lastUpdate).isBefore(moment().subtract(5, "minutes"))
      );
    },
    reducedData() {
      return this.filteredData.payments.reduce(
        (acc, payment) => {
          const utm = payment.utm;

          Object.entries(utm).forEach(([key, value]) => {
            if (!acc[key] || !value) return;
            if (!acc[key][value]) {
              acc[key][value] = {
                name: value,
                amount: 0,
                tickets: 0,
                payments: [],
              };
            }
            acc[key][value].amount += payment.amount - payment.platformFee;
            acc[key][value].tickets += payment.Ticket.length;
            acc[key][value].payments.push(payment);
          });

          return acc;
        },
        {
          campaign: {},
          source: {},
          medium: {},
          term: {},
          content: {},
        }
      );
    },
    filteredData() {
      const { payments, tickets } = this.payments.reduce(
        (acc, payment) => {
          if (payment.utm && Object.keys(payment.utm).length > 0) {
            acc.payments.push(payment);
            acc.tickets.push(
              ...payment.Ticket.map((t) => ({ ...t, Payment: payment }))
            );
          }
          return acc;
        },
        { payments: [], tickets: [] }
      );

      return { payments, tickets };
    },
  },
  methods: {
    async exportExcel() {
      try {
        this.exporting = "excel";

        await exportExcel({
          party: this.party,
          tickets: this.filteredData.tickets,
          payments: this.filteredData.payments,
          filters: this.filters,
          ticketBlocksMap: this.ticketBlockMap,
        });
      } catch (error) {
        console.error(error);
      } finally {
        this.exporting = false;
      }
    },
  },
  props: {
    party: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String | Boolean | null,
      default: false,
    },
    lastUpdate: {
      type: Date,
      default: null,
    },
    blockRefresh: {
      type: Boolean,
      default: false,
    },
    refreshKey: {
      type: Number,
      default: 0,
    },
    tickets: {
      type: Array,
      default: () => [],
    },
    payments: {
      type: Array,
      default: () => [],
    },
    ticketGroups: {
      type: Array,
      default: () => [],
    },
    posSellers: {
      type: Array,
      default: () => [],
    },
    saleLinks: {
      type: Array,
      default: () => [],
    },
    sellers: {
      type: Array,
      default: () => [],
    },
    ticketBlockMap: {
      type: Map,
      default: () => new Map(),
    },
  },
};
</script>
